import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { beep } from "../lib/sound";

const PHILOSOPHY = {
  en: {
    label: "EN",
    kicker: "OUR PHILOSOPHY",
    title: "ABSOLUTE PURITY",
    body: [
      "True prestige cannot be manufactured. Shaped by the meticulous soul of Korean craftsmanship, our philosophy rests on an unwavering devotion to natural perfection.",
      "We source only the highest tier materials in their pure and unadulterated state, strictly refusing any artificial coloring. By merging advanced precision engineering with traditional ring making, we elevate every piece of jewelry into a marvel of intelligent structural design.",
      "Each creation is entirely forged by hand, guided by artisans who understand that genuine luxury demands patience and absolute exactitude.",
    ],
  },
  ko: {
    label: "한국어",
    kicker: "우리의 철학",
    title: "절대적 순수함의 권위",
    body: [
      "진정한 명성은 만들어지는 것이 아닙니다. 독일 엔지니어링의 정밀함과 한국 장인정신의 섬세한 영혼을 결합하여, 우리의 철학은 자연스러운 완벽함을 향한 확고한 헌신에 기반을 두고 있습니다.",
      "우리는 어떠한 인위적인 착색도 엄격히 배제하며, 순수한 상태의 최고급 소재만을 고집합니다.",
      "모든 작품은 진정한 럭셔리가 인내와 절대적인 정밀함을 요구한다는 것을 이해하는 장인들의 손길을 거쳐 온전히 수작업으로 탄생합니다.",
    ],
  },
  zh: {
    label: "中文",
    kicker: "我们的理念",
    title: "绝对纯粹的权威",
    body: [
      "真正的声望是无法制造的。将德国工程的精确性与韩国工艺的一丝不苟相结合，我们的理念建立在对自然完美坚定不移的追求之上。",
      "我们只采购处于纯粹状态的最高级别材料，严格拒绝任何人工染色。",
      "每一件作品均由深知真正的奢华需要耐心与绝对精准的工匠完全手工锻造而成。",
    ],
  },
  es: {
    label: "ES",
    kicker: "NUESTRA FILOSOFÍA",
    title: "LA AUTORIDAD DE LA PUREZA ABSOLUTA",
    body: [
      "El verdadero prestigio no se puede fabricar. Al unir la exactitud de la ingeniería alemana con el alma meticulosa de la artesanía coreana, nuestra filosofía se basa en una devoción inquebrantable por la perfección natural.",
      "Obtenemos solo los materiales del más alto nivel en su estado puro, rechazando estrictamente cualquier coloración artificial.",
      "Cada creación es forjada a mano en su totalidad, guiada por artesanos que comprenden que el verdadero lujo exige paciencia y precisión absoluta.",
    ],
  },
};

export default function Manifesto() {
  const [lang, setLang] = useState("en");
  const t = PHILOSOPHY[lang];
  return (
    <div data-testid="manifesto-section" className="relative bg-black">
      <section
        id="philosophy"
        data-testid="philosophy-section"
        className="relative flex min-h-screen items-center overflow-hidden"
      >
        <img
          src="/textures/philosophy.png"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full scale-105 object-cover blur-[3px] brightness-[0.55]"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.75),rgba(0,0,0,0.25)_60%,rgba(0,0,0,0.6))]" />
        <div className="scanlines-layer pointer-events-none absolute inset-0 opacity-20" />

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative mx-auto w-full max-w-6xl px-6 py-28 sm:px-10"
        >
          <div className="flex flex-wrap items-center gap-4">
            <p className="font-mono text-[10px] tracking-[0.4em] text-white/60">OUR PHILOSOPHY // 哲学</p>
            <div className="flex gap-1" data-testid="philosophy-lang-switch">
              {Object.entries(PHILOSOPHY).map(([code, v]) => (
                <button
                  key={code}
                  data-testid={`philosophy-lang-${code}`}
                  aria-pressed={lang === code}
                  onClick={() => {
                    beep(700, 0.05);
                    setLang(code);
                  }}
                  className={`btn-y2k px-2 py-0.5 text-[9px] ${lang === code ? "brightness-75" : ""}`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
          <h2
            data-testid="philosophy-title"
            className="mt-4 max-w-3xl font-display text-4xl font-extrabold uppercase tracking-tight text-white sm:text-5xl lg:text-6xl"
          >
            ABSOLUTE PURITY
          </h2>
          <AnimatePresence mode="wait">
            <motion.div
              key={lang}
              data-testid="philosophy-body"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 min-h-[16rem] max-w-2xl space-y-5 font-mono text-sm leading-relaxed text-white/85 sm:min-h-[15rem] sm:text-base"
            >
              {t.body.map((p, i) => (
                <p key={p.slice(0, 40)} className={i === t.body.length - 1 ? "text-white" : ""}>
                  {p}
                </p>
              ))}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </section>
    </div>
  );
}
